import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import NumberFormat from 'react-number-format';
import {
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField
} from '@material-ui/core';
import { Formik, FieldArray } from 'formik';
import {
  BackdropCircular,
  Breadcrumb,
  DesignedConfirmDialog,
  DesignedInfoDialog,
  InfoTooltip,
  TextFormattedNumber
} from 'components';
import {
  refundOrderApi,
  getInvoiceOrderDataApi,
  getOrderProductsBySkusApi
} from 'api';
import round from 'helpers/mathRound';
import OrderItemDetails from './OrderItemDetails';
import PaymentMerchants from './PaymentMerchants';
import ModMedRefundInfoDialog from './ModMedRefundInfoDialog';
import { orderRefundValidationSchema } from './orderRefundValidationSchema';
import { useStyles } from './styles';


const paymentMethodsToRefundOnline = [
  'bluepay_payment',
  'nextech_payment',
  'cc_split',
  'cc_saved_split',
  'multi_cc_split',
  'modmed_payment',
  'simpay_payment'
];

const fullRefundOnlineMethods = [
  'multi_cc_split'
];

const OrderRefund = ({location: {state: {order, orderItemsDetails}}}) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  const [refundData, setRefundData] = useState<IRefundData>(null);
  const [openConfirmRefund, setOpenConfirmRefund] = useState(false);
  const [openRefundOffline, setOpenRefundOffline] = useState(false);
  const [openModMedPopup, setOpenModMedPopup] = useState(false);

  useEffect(() => {
    getInvoiceOrderDataApi({
      dispatch,
      sortDirection: 'asc',
      sortField: 'entity_id',
      pageSize: 100,
      currentPage: 1,
      filterConditions: [{
        field: 'order_id',
        value: order.entity_id,
        conditionType: 'eq'
      }],
      searchConditions: []
    });

    if (!orderProductsDetails.length) {
      const skus = order.items.map(({ sku }) => sku) || [];
      getOrderProductsBySkusApi({dispatch, skus});
    }
  }, []);

  const {
    invoiceOrderData,
    orderProductsDetails,
    loadingInvoiceOrderData,
    loadingOrderRefund,
    loadingOrderProductsDetails
  } = useSelector((state: IReduxState) => state.store_orders);

  const {
    increment_id,
    discount_description,
    total_paid,
    subtotal,
    items,
    base_shipping_amount,
    extension_attributes: {
      mp_reward,
      shipping_assignments: [{
        shipping: {
          method: shippingMethod,
          total: {shipping_amount}
        }
      }]
    },
    shipping_description,
    payment: {
      method
    }
  } = order;

  const processingFee = order?.extension_attributes?.fooman_total_group?.items
    ?.find((item) => item?.label?.toLowerCase().includes('processing fee'))
    ?.amount || 0;

  const totalRefunded = order?.total_refunded || 0;

  const setShippingMethod = () => {
    if (shippingMethod === 'flatrate_flatrate') {
      return `${shipping_description} $${shipping_amount.toFixed(2)}`;
    }

    return shipping_description;
  };

  const handleSubmitRefund = (values) => {
    const {
      items,
      append_comment,
      comment_text,
      notify,
      shipping_amount,
      adjustment_positive,
      adjustment_negative,
      refund_processing_fee,
    } = values;

    const {
      order_id,
      entity_id
    } = invoiceOrderData;

    const itemsToRefund = items.reduce((acc, item) => {
      return [
        ...acc,
        {
          item_id: item.item_id,
          qty: isAffiliate(item) ? 0 : item.qty_to_refund,
          back_to_stock: item.back_to_stock ? 1 : 0
        }
      ];
    }, []);

    const refund = {
      order_id,
      invoice_id: entity_id,
      do_offline: paymentMethodsToRefundOnline.includes(method) ? 0 : 1,
      comment_text,
      comment_customer_notify: append_comment ? 1 : 0,
      is_visible_on_front: append_comment ? 1 : 0,
      send_email: notify ? 1 : 0,
      shipping_amount: Number(shipping_amount),
      adjustment_positive: Number(adjustment_positive),
      adjustment_negative: Number(adjustment_negative) + Number(processingFee - refund_processing_fee || 0),
      items: itemsToRefund
    };
    setRefundData(refund);
    setOpenConfirmRefund(true);
  };

  const handleConfirmRefund = async () => {
    if (method === 'modmed_payment') {
      setOpenModMedPopup(true);
      return;
    }

    const isRefundSuccess = await refundOrderApi({dispatch, refundData});
    const isOfflineRefund = isRefundSuccess && !paymentMethodsToRefundOnline.includes(method);

    if (!isOfflineRefund && isRefundSuccess) {
      history.push('/orders/list');
    }

    if (isOfflineRefund && isRefundSuccess) {
      setOpenRefundOffline(true);
    }
  };

  const countUsedGiftcards = (orderItem) => {
    const usedGiftCards = orderItem?.gift_cards?.filter((card) => card.init_balance !== card.balance) || [];

    return usedGiftCards.length;
  };

  const isAffiliate = (item) => {
    const value = Array.isArray(orderProductsDetails) && orderProductsDetails.some((product) => {
      const hasSku = product.sku === item?.sku;
      const hasExternalServiceType = product.custom_attributes.some((attr) => attr.attribute_code === 'external_service_type' && attr.value === '1');
      return hasSku && hasExternalServiceType;
    });

    return Boolean(value);
  };

  return (
    <div className="flex-grow flex-column relative h-full scroll-y pb-5">
      <div className="mb-sm-30">
        <Breadcrumb
          routeSegments={[
            { name: 'Orders', path: '/orders/list'},
            { name: `Refund Order #${increment_id}`}
          ]}
        />
      </div>
      <Formik
        initialValues={{
          shipping_amount: base_shipping_amount - (order?.shipping_refunded || 0),
          adjustment_negative: 0,
          adjustment_positive: 0,
          items: items
            .map((item, index) => {
              let qty_to_refund = item.qty_invoiced - item.qty_refunded;

              if (item.product_type === 'mpgiftcard') {
                const usedGiftCardsCount = countUsedGiftcards(orderItemsDetails?.[index]);
                qty_to_refund = qty_to_refund - usedGiftCardsCount;
              }

              isAffiliate(item) ? qty_to_refund = 0 : qty_to_refund;

              return {
                ...item,
                qty_to_refund,
                back_to_stock: false
              };
            }),
          refund_processing_fee: processingFee,
          base_shipping_amount,
          comment_text: '',
          notify: false,
          append_comment: false,
          mp_reward
        }}
        enableReinitialize
        validationSchema={orderRefundValidationSchema(orderItemsDetails)}
        onSubmit={handleSubmitRefund}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          setFieldTouched
        }) => {
          const {
            comment_text,
            shipping_amount,
            adjustment_positive,
            adjustment_negative,
            append_comment,
            notify,
            mp_reward
          } = values;

          const spendPointsOnShipping = shipping_amount > base_shipping_amount
            ? (mp_reward?.mp_reward_shipping_discount || 0)
            : ((mp_reward?.mp_reward_shipping_discount || 0) / base_shipping_amount) * Number(shipping_amount) || 0;

          const {
            refundSubtotal,
            taxAmount,
            earnPointsTotal,
            spendPointsDiscount,
            spendPointsTotal,
            discountAmount
          } = values.items.reduce((acc, item) => {
            const {
              price,
              tax_amount,
              qty_ordered,
              discount_amount,
              extension_attributes: {mp_reward: {
                mp_reward_earn,
                mp_reward_spent,
                mp_reward_discount
              }}
            } = item;

            if (!!item?.qty_to_refund) {
              const { qty_to_refund } = item;
              const itemRefundPrice = !qty_to_refund ? 0 :  qty_to_refund * price;
              const itemQtyRefund = !qty_to_refund ? 0 : qty_to_refund;
              const itemRefundTax = !qty_to_refund
                ? 0
                : ((tax_amount || 0) / qty_ordered) * itemQtyRefund;
              const earnPoints = !qty_to_refund ? 0 : (mp_reward_earn / qty_ordered) * qty_to_refund;
              const spendPointsDiscount = !qty_to_refund ? 0 : (mp_reward_discount / qty_ordered) * qty_to_refund;
              const spendPoints = !qty_to_refund ? 0 : (mp_reward_spent / qty_ordered) * qty_to_refund;
              acc['refundSubtotal'] = acc['refundSubtotal'] + itemRefundPrice;
              acc['taxAmount'] = acc['taxAmount'] + ((itemRefundTax * 100) / 100);
              acc['itemsCountToRefund'] = acc['itemsCountToRefund'] + itemQtyRefund;
              acc['itemsOrdered'] = acc['itemsOrdered'] + qty_ordered;
              acc['earnPointsTotal'] = acc['earnPointsTotal'] + earnPoints;
              acc['spendPointsDiscount'] = acc['spendPointsDiscount'] + spendPointsDiscount;
              acc['spendPointsTotal'] = acc['spendPointsTotal'] + spendPoints;
              acc['discountAmount'] = acc['discountAmount'] + ((discount_amount / qty_ordered) * itemQtyRefund);
              return acc;
            }

            return acc;
          }, {
            refundSubtotal: 0,
            taxAmount: 0,
            itemsCountToRefund: 0,
            itemsOrdered: 0,
            earnPointsTotal: 0,
            spendPointsDiscount: 0,
            spendPointsTotal: 0,
            discountAmount: 0,
            amountRefunded: 0
          });

          const creditTotal = ((order.extension_attributes?.credit || 0) / subtotal) * refundSubtotal || 0;
          const giftCardTotal = ((order.extension_attributes?.gift_card_value || 0) / subtotal) * refundSubtotal || 0;
          const giftCardCode = order.extension_attributes?.gift_card_code || '';

          const discount = (refundSubtotal - Math.abs(creditTotal) - Math.abs(discountAmount) - Math.abs(giftCardTotal) - Math.abs(spendPointsDiscount) - spendPointsOnShipping).toFixed(2);
          const grandTotalNotRounded = Number(discount) + Number(shipping_amount) + Number(adjustment_positive) - Number(adjustment_negative) + Number(values.refund_processing_fee || 0) + Number(taxAmount.toFixed(2));
          const grandTotal = Number(grandTotalNotRounded.toFixed(2));

          const amountToRefund = Number((total_paid - totalRefunded).toFixed(2));
          const isOverRefund = (grandTotal > amountToRefund);
          const isRefundDeclined = (grandTotal < 0) || isOverRefund;

          const infoGrandTotalValue = grandTotal < 0
            ?  `-$${Math.abs((grandTotal - totalRefunded)).toFixed(2)}`
            :  `$${(grandTotal - totalRefunded).toFixed(2)}`;

          const infoTooltipValue = isOverRefund ? `$${amountToRefund}` : infoGrandTotalValue;

          return (
            <form onSubmit={handleSubmit} noValidate>
              <Grid container spacing={3} className="px-8">
                <Grid item xs={12}>
                  <div className={classes.refundCard}>
                    <Table className={classes.refundTable}>
                      <TableHead>
                        <TableRow>
                          <TableCell width="20%">Products</TableCell>
                          <TableCell>Price</TableCell>
                          <TableCell>Qty</TableCell>
                          <TableCell>Return to Stock</TableCell>
                          <TableCell>Qty To Refund</TableCell>
                          <TableCell>Subtotal</TableCell>
                          <TableCell>Tax Amount</TableCell>
                          <TableCell>Discount</TableCell>
                          <TableCell>Row Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <FieldArray name="items">
                          {() => (
                            <>
                              {values.items.map((item, index) => {
                                const {
                                  name,
                                  price,
                                  qty_ordered,
                                  qty_invoiced,
                                  qty_refunded,
                                  qty_shipped,
                                  qty_canceled,
                                  tax_amount,
                                  discount_amount,
                                  back_to_stock
                                } = item;
                                const usedGiftCardsCount = countUsedGiftcards(orderItemsDetails?.[index]);


                                const rowSubtotal = price * (item?.qty_to_refund ? item.qty_to_refund : 0);
                                const rowTaxAmount = (tax_amount / qty_ordered) * (item?.qty_to_refund ? item.qty_to_refund : 0);
                                const rowDiscountAmount = (discount_amount / qty_ordered) * (item?.qty_to_refund ? item.qty_to_refund : 0);
                                const isError = touched.items &&
                                  errors.items?.length &&
                                  touched.items?.[index]?.qty_to_refund &&
                                  Boolean(errors.items?.[index]?.qty_to_refund);
                                const errorText = touched.items &&
                                  errors.items?.length &&
                                  touched.items?.[index]?.qty_to_refund &&
                                  errors.items?.[index]?.qty_to_refund;
                                return (
                                  <TableRow key={item.item_id}>
                                    <TableCell>
                                      <div>{name}</div>
                                      <OrderItemDetails
                                        item={item}
                                        itemDetails={orderItemsDetails?.[index]}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <TextFormattedNumber
                                        value={price}
                                        prefix="$"
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <div>{`Ordered ${qty_ordered}`}</div>
                                      <div>{`Invoiced ${qty_invoiced}`}</div>
                                      {!!qty_refunded &&
                                      <div>{`Refunded ${qty_refunded}`}</div>
                                      }
                                      {!!qty_shipped &&
                                      <div>{`Shipped ${qty_shipped}`}</div>
                                      }
                                      {!!qty_canceled &&
                                      <div>{`Canceled ${qty_canceled}`}</div>
                                      }
                                      {!!usedGiftCardsCount &&
                                      <div>{`Used ${usedGiftCardsCount}`}</div>
                                      }
                                    </TableCell>
                                    <TableCell>
                                      {orderItemsDetails?.[index].is_manage_stock &&
                                        <Checkbox
                                          color="primary"
                                          checked={back_to_stock}
                                          onChange={({target: {checked}}) => {
                                            setFieldValue(
                                              `items[${index}].back_to_stock`,
                                              checked);
                                          }}
                                        />
                                      }
                                    </TableCell>
                                    <TableCell>
                                      <TextField
                                        name={`items[${index}].qty_to_refund`}
                                        variant="outlined"
                                        value={values.items[index]?.qty_to_refund || 0}
                                        disabled={isAffiliate(values.items[index]) || fullRefundOnlineMethods.includes(method)}
                                        inputProps={{
                                          className: 'text-right w-full p-2 text-12',
                                        }}
                                        error={isError}
                                        helperText={errorText}
                                        onChange={({target: {value, name}}) => setFieldValue(name, +value)}
                                        onBlur={({target: {name}}) => setFieldTouched(name, true)}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <TextFormattedNumber
                                        value={rowSubtotal}
                                        prefix="$"
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <TextFormattedNumber
                                        value={rowTaxAmount}
                                        prefix="$"
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <TextFormattedNumber
                                        value={rowDiscountAmount}
                                        prefix="$"
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <TextFormattedNumber
                                        value={rowSubtotal + rowTaxAmount - rowDiscountAmount}
                                        prefix="$"
                                      />
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </>
                          )}
                        </FieldArray>
                      </TableBody>
                    </Table>
                  </div>
                </Grid>
                <Grid item xs={12} md={4} className="flex-auto">
                  <div className={`pt-4 pb-6 px-6 h-full ${classes.refundCard}`}>
                    <h4 className="text-primary mb-8">Comments</h4>
                    <TextField
                      multiline
                      minRows={4}
                      label="Comment Text"
                      variant="outlined"
                      fullWidth
                      name="comment_text"
                      value={comment_text}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <div className="flex-column mt-4">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={append_comment}
                            color="primary"
                            size="small"
                            onChange={({target: {checked}}) => setFieldValue('append_comment', checked)}
                            disabled={!comment_text.length}
                          />}
                        label="Append Comments"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={notify}
                            color="primary"
                            size="small"
                            onChange={({target: {checked}}) => setFieldValue('notify', checked)}
                          />}
                        label="Email Copy of Refund"
                      />
                    </div>
                  </div>
                </Grid>
                <Grid item xs={12} md={4} className="flex-auto">
                  <div className={`pt-4 pb-6 px-6 h-full ${classes.refundCard}`}>
                    <h4 className="text-primary mb-8">Shipping Information</h4>
                    <div className="font-bold">{setShippingMethod()}</div>
                    <div className="my-3">{`Total Shipping Charges: $${Number(base_shipping_amount).toFixed(2)}`}</div>
                    {!!order?.shipping_refunded &&
                      <div>
                        {`Total Shipping Refunded: $${Number(order.shipping_refunded).toFixed(2)}`}
                      </div>
                    }
                  </div>
                </Grid>
                <Grid item xs={12} md={4} className="flex-auto text-12">
                  <div className={`pt-4 pb-1 px-6 h-full ${classes.refundCard}`}>
                    <h4 className="text-primary mb-4">Refund Totals</h4>
                    <div className="flex items-center justify-between py-2">
                      <div>Subtotal</div>
                      <div>
                        <TextFormattedNumber
                          value={refundSubtotal}
                          prefix="$"
                        />
                      </div>
                    </div>
                    {!!earnPointsTotal &&
                    <div className="flex items-center justify-between py-2">
                      <div>Earned</div>
                      <div>
                        {`${earnPointsTotal.toFixed(0)} points`}
                      </div>
                    </div>}
                    {!!spendPointsTotal &&
                    <div className="flex items-center justify-between py-2">
                      <div>Spend</div>
                      <div>
                        {`${spendPointsTotal.toFixed(0)} points`}
                      </div>
                    </div>}
                    {!!creditTotal &&
                    <div className="flex items-center justify-between py-2">
                      <div>Credit</div>
                      <div>
                        <TextFormattedNumber
                          value={creditTotal}
                          prefix="$"
                        />
                      </div>
                    </div>}
                    {(!!spendPointsDiscount || !!spendPointsOnShipping) &&
                    <div className="flex items-center justify-between py-2">
                      <div>Spend Points</div>
                      <div>
                        <TextFormattedNumber
                          value={spendPointsDiscount + spendPointsOnShipping}
                          prefix="-$"
                        />
                      </div>
                    </div>}
                    {!!discountAmount &&
                    <div className="flex items-center justify-between py-2">
                      <div>{`Discount (${discount_description})`}</div>
                      <div>
                        <TextFormattedNumber
                          value={discountAmount}
                          prefix="-$"
                        />
                      </div>
                    </div>}
                    {!!giftCardTotal &&
                    <div className="flex items-center justify-between py-2">
                      <div>{`Gift Cards (${giftCardCode})`}</div>
                      <div>
                        <TextFormattedNumber
                          value={giftCardTotal}
                          prefix="$"
                        />
                      </div>
                    </div>}
                    <div className="flex items-center justify-between py-2">
                      <div>
                        {'Refund Shipping'}
                        <InfoTooltip
                          title={`In the Refund Shipping field, enter an amount to be
                           refunded from the shipping fee. This field initially
                           shows the total shipping amount from the order that is
                            available for refund. It equals the initial shipping
                             amount minus any shipping amount that has already been
                              refunded. The amount can be decreased, but not increased,
                               like the quantity.`}
                        />
                      </div>
                      <div>
                        <NumberFormat
                          customInput={TextField}
                          allowNegative={false}
                          name="shipping_amount"
                          variant="outlined"
                          className="max-w-60"
                          value={shipping_amount}
                          disabled={fullRefundOnlineMethods.includes(method)}
                          inputProps={{
                            className: 'text-right p-2 text-12'
                          }}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.shipping_amount && Boolean(errors.shipping_amount)}
                        />
                      </div>
                    </div>
                    {Boolean(errors.shipping_amount) &&
                    <div className="w-full flex justify-end">
                      <FormHelperText className="text-error">
                        {errors.shipping_amount}
                      </FormHelperText>
                    </div>
                    }
                    <div className="flex items-center justify-between py-2">
                      <div>
                        {'Adjustment Refund'}
                        <InfoTooltip
                          title={`In the Adjustment Refund field, enter an amount
                          that will be added to the total; amount refunded as an
                            additional refund beyond shipping, product price, and
                            tax cost. The amount entered can not increase the total
                              refund greater than the paid amount.`}
                        />
                      </div>
                      <div>
                        <NumberFormat
                          customInput={TextField}
                          allowNegative={false}
                          name="adjustment_positive"
                          variant="outlined"
                          className="max-w-60"
                          value={adjustment_positive || 0}
                          disabled={fullRefundOnlineMethods.includes(method)}
                          inputProps={{
                            className: 'text-right p-2 text-12'
                          }}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <div>
                        {'Adjustment Fee'}
                        <InfoTooltip
                          title={`In the Adjustment Fee field, enter a value to be
                           deducted from the total amount refunded. The amount is
                            not deducted from a particular part of the items such
                             as shipping, product price, or tax.`}
                        />
                      </div>
                      <div>
                        <NumberFormat
                          customInput={TextField}
                          allowNegative={false}
                          name="adjustment_negative"
                          variant="outlined"
                          className="max-w-60"
                          value={adjustment_negative || 0}
                          disabled={fullRefundOnlineMethods.includes(method)}
                          inputProps={{
                            className: 'text-right p-2 text-12'
                          }}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </div>
                    </div>
                    {!!values.refund_processing_fee &&
                    <div className="flex items-center justify-between py-2">
                      <div>
                        {'Credit Card Processing Fee'}
                        <InfoTooltip
                          title="Enter the amount of the credit card processing fee to be refunded (deducted from customer refund). Default is full fee."
                        />
                      </div>
                      <div>
                        <NumberFormat
                          customInput={TextField}
                          allowNegative={false}
                          name="refund_processing_fee"
                          variant="outlined"
                          className="max-w-60"
                          value={values.refund_processing_fee || 0}
                          disabled={fullRefundOnlineMethods.includes(method)}
                          inputProps={{
                            className: 'text-right p-2 text-12'
                          }}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </div>
                    </div>}
                    {!!taxAmount &&
                    <div className="flex items-center justify-between py-2">
                      <div>Tax</div>
                      <div>
                        <TextFormattedNumber
                          value={round(taxAmount)}
                          prefix="$"
                        />
                      </div>
                    </div>}
                    <Divider className="my-2" />
                    <div className="flex items-center justify-between py-2 mb-2 text-14 font-bold">
                      <div>
                        {'Grand Total'}
                        {isRefundDeclined &&
                          <InfoTooltip
                            title={`Grand Total can't
                              be ${isOverRefund ? 'more than' : ''}
                              ${infoTooltipValue}`
                            }
                            classNames="text-error"
                          />}
                      </div>
                      <div>
                        <TextFormattedNumber
                          value={grandTotal}
                          prefix="$"
                        />
                      </div>
                    </div>
                  </div>
                </Grid>
              </Grid>
              <Grid container spacing={3} className="p-8">
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="contained"
                    onClick={() => history.goBack()}
                    className="w-100 uppercase"
                  >
                    Back
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} className="flex justify-end w-full">
                  <Button
                    color="primary"
                    variant="contained"
                    type="submit"
                    className="w-100 uppercase"
                    disabled={isRefundDeclined}
                  >
                    Refund
                  </Button>
                </Grid>
              </Grid>
            </form>
          );
        }}
      </Formik>
      <DesignedConfirmDialog
        open={openConfirmRefund}
        setOpen={setOpenConfirmRefund}
        onConfirm={handleConfirmRefund}
      >
        Are you sure you want to refund this order?
      </DesignedConfirmDialog>
      <DesignedInfoDialog
        open={openRefundOffline}
        setOpen={() => history.push('/orders/list')}
      >
        <div>
          <div className="mb-3">
            Your order status was changed into &quot;Closed&quot;.
          </div>
          {method !== 'free' &&
            <>
              <div className="mb-3">
                In order to refund, you need to login to your Merchant Account.
              </div>
              <div className="mb-5">
                Select your account provider below to login:
              </div>
              <PaymentMerchants paymentMethod={method} />
            </>
          }
        </div>
      </DesignedInfoDialog>
      <ModMedRefundInfoDialog
        open={openModMedPopup}
        onClose={() => setOpenModMedPopup(false)}
      />
      <BackdropCircular loading={loadingInvoiceOrderData || loadingOrderRefund || loadingOrderProductsDetails} />
    </div>
  );
};

export default OrderRefund;
